import { useMemo } from 'react'
import { useIsLiveFinalRevealEnabled } from '../../../../hooks/use-live-final-reveal'
import { trimToStableStreamingText } from '../../../../utils/stable-streaming-text'
import { useHeldAnswers } from './use-held-answers'
import type { ResolvedPart } from './use-interleaved-parts'

export interface AnswerReveal {
  /** React key: held answers by their watermark key, the live answer by a constant so it stays mounted. */
  key: string
  text: string
  revealKey: string
  /** The answer still streaming or just finished, which owns the rendered-content ref. */
  isLive: boolean
}

interface UseAnswerRevealsOptions {
  renderParts: Array<{ type: 'text'; text: string } | { type: 'tool_call' }>
  interleavedParts: Array<ResolvedPart>
  trailingTextParts: Array<ResolvedPart & { type: 'text' }>
  finalText: string
  isStreaming: boolean
  messageId: string
  /** Whether a finished answer animates: it streamed in this session. */
  animatesAfterStream: boolean
}

/**
 * Where the answer renders and how it reveals. By default the streaming answer
 * rides inside the fold and reveals once the turn ends. With the live reveal
 * it reveals in place from the first token, and answers a tool call folds
 * stay in place for a while (see `useHeldAnswers`).
 */
export function useAnswerReveals({
  renderParts,
  interleavedParts,
  trailingTextParts,
  finalText,
  isStreaming,
  messageId,
  animatesAfterStream,
}: UseAnswerRevealsOptions): {
  toolListParts: Array<ResolvedPart>
  answers: Array<AnswerReveal>
  shouldAnimate: boolean
} {
  const liveReveal = useIsLiveFinalRevealEnabled()

  // A live answer segment that a later tool call moved into the fold keeps
  // its revealed count; the next segment gets its own watermark so its first
  // words reveal instead of landing already shown. Text before an ask-user
  // call stays the answer, so it does not count as moved.
  const foldedAnswerSegments = useMemo(
    () => renderParts.filter((part) => part.type === 'text' && part.text?.trim()).length - trailingTextParts.length,
    [renderParts, trailingTextParts],
  )
  const revealKey = liveReveal && foldedAnswerSegments > 0 ? `${messageId}:${foldedAnswerSegments}` : messageId

  const heldAnswers = useHeldAnswers({
    enabled: liveReveal,
    isStreaming,
    interleavedParts,
    trailingTextParts,
    finalText,
    revealKey,
  })

  // Identity-stable: `useInterleavedParts` returns the same arrays while
  // nothing changed, so this memo (and `ToolCallList`'s memo behind it) only
  // fires on real change. Held answers stay out of the fold until their hold
  // ends.
  const trailingInFold = isStreaming && !liveReveal
  const toolListParts = useMemo<Array<ResolvedPart>>(() => {
    if (trailingInFold) return [...interleavedParts, ...trailingTextParts]
    if (heldAnswers.length === 0) return interleavedParts
    return interleavedParts.filter((part) => !heldAnswers.some((answer) => answer.partKeys.has(part.stableKey)))
  }, [trailingInFold, interleavedParts, trailingTextParts, heldAnswers])

  // The live answer shows only what the next token can't reshape. Held
  // answers are finished steps, so they show in full.
  const liveText = isStreaming && liveReveal ? trimToStableStreamingText(finalText) : finalText
  const answers = useMemo<Array<AnswerReveal>>(
    () => [
      ...heldAnswers.map((answer) => ({
        key: answer.revealKey,
        text: answer.text,
        revealKey: answer.revealKey,
        isLive: false,
      })),
      ...(liveText && !trailingInFold ? [{ key: 'live', text: liveText, revealKey, isLive: true }] : []),
    ],
    [heldAnswers, liveText, trailingInFold, revealKey],
  )

  return { toolListParts, answers, shouldAnimate: (liveReveal && isStreaming) || animatesAfterStream }
}
